/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import ConnectionError from '../../../../shared/ErrorComponents/ConnectionError';
import withErrorStatus from '../../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../../shared/StatusWrappers/LoadingStatus';
import useConnectorAndDynamicProps from '../../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import { useInterval } from '../../../../shared/useInterval/useInterval';
import useMounted from '../../../../shared/useMount/useMount';
import {
  POLLING_SECONDS,
  POLLING_MILLI_SECONDS,
  UtilityType,
  IntervalType
} from '../../../../shared/Utils/Constants';
import { isObjectOrArrayAndEmpty } from '../../../../shared/Utils/Utils';
import {
  ISiteCard,
  IEmaasWaterUsageHOCProps,
  IEuiSummary,
  IEmaasWaterUsageComponentData,
  IAny
} from '../../../IEnergyManagementProps';
import EmaasBasicSubscriptionEnergyUsageComponent from '../../../EmaasEnergyUsage/EmaasBasicSubscriptionEnergyUsageComponent/EmaasBasicSubscriptionEnergyUsageComponent';
import {
  EmaasSubscriptionType,
  EnergyUsageField
} from '../../../../shared/Enums';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import {
  IPollingContext,
  PollingContext
} from '../../../../../context/PollingContext/PollingContext';
import { getIntervalType } from '../../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import moment from 'moment';
import NoData from '../../../../shared/ErrorComponents/NoData';
import { ApiContext } from '../../../../../context/ApiContext';
import BreakdownByMeter from '../../../../shared/BreakdownByMeter/BreakdownByMeter';
import PortfolioEnergyCardSeparate from '../../PortfolioEnergyWidget/PortfolioEnergyCard/PortfolioEnergyCardSeparate';
import { Card } from '@scuf/common';
import PortfolioWaterCardSeparate from '../../PortfolioEnergyWidget/PortfolioEnergyCard/PortfolioWaterCardSeparate';

interface IPortfolioWaterHOCContentSeparate extends ISiteCard {
  data: IEmaasWaterUsageComponentData;
  forSummary?: boolean;
  forGraph?: boolean;
  forTable?: boolean;
}

const PortfolioWaterHOCContentSeparate = (
  props: IPortfolioWaterHOCContentSeparate
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div>
      <PortfolioWaterCardSeparate
        forSummary={props.forSummary}
        forGraph={props.forGraph}
        forTable={props.forTable}
        data={data}
        viewType={EnergyUsageField.PORTFOLIOWATER}
      />
    </div>
  );
};

const PortfolioWaterHOCWithLoader = withLoadingStatus(
  withErrorStatus(PortfolioWaterHOCContentSeparate)
);

// Refresh every hour
const ENERGYUSAGE_REFRESH_INTERVAL = POLLING_SECONDS * POLLING_MILLI_SECONDS;

const PortfolioWaterHOCSeparate = (props: IEmaasWaterUsageHOCProps) => {
  const [silentLoading, setIsSilentLoading] = useState<boolean>(false);
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const [pollingCount, setPollingCount] = useState(0);
  const { bearerToken, requestContext } = props;

  const apiContext = useContext(ApiContext);

  const isSitePremium =
    requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    apiContext.toggleSeparateApi;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);

  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!
  );

  const isDateRangeValid = () => {
    const sDate = moment(
      getEnergyStartDate(
        requestContext.context.startTime!,
        requestContext.context.subscriptionType!
      ),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      getEnergyEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  const connectorConfig = {
    name: isSitePremium ? 'emaas-energy-usage-summary' : 'emaas-energy-usage',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: 'Water',
      intervalType: IntervalType.Monthly
    }
  };

  const connectorConfigPerformanceAggregation = {
    name: 'emaas-energy-usage-performance-aggregation',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: 'Water',
      intervalType: IntervalType.Monthly
    }
  };

  const connectorConfigTable = {
    name: 'emaas-energy-usage-table',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: 'Water',
      groupType: 'Site',
      intervalType: IntervalType.Monthly
    }
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  let dataConnectorPerformanceAggregation: any;
  if (isSitePremium) {
    dataConnectorPerformanceAggregation = useConnectorAndDynamicProps({
      connectorConfig: connectorConfigPerformanceAggregation,
      requestContext,
      bearerToken
    })[0];
  }

  let dataConnectorTable: any;
  if (isSitePremium) {
    dataConnectorTable = useConnectorAndDynamicProps({
      connectorConfig: connectorConfigTable,
      requestContext,
      bearerToken
    })[0];
  }

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [pollingCount]);

  useEffect(() => {
    setIsSilentLoading(false);
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        onRefresh();
        onRefreshPerf();
        onRefreshTable();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  const allEnergy = dataConnector.data;

  const EmaasEnergyUsage = (allEnergy as unknown) as IEmaasWaterUsageComponentData;

  // Polling mechanism
  useInterval(() => {
    if (
      EmaasEnergyUsage &&
      EmaasEnergyUsage.subscriptionType === EnergyUsageField.PREMIUMSUBS
    ) {
      setIsSilentLoading(true);
      setPollingCount(pollingCount + 1);
    }
  }, ENERGYUSAGE_REFRESH_INTERVAL);

  const pollingContext = useContext(PollingContext);

  const onRefreshPerf = () =>
    dataConnectorPerformanceAggregation &&
    dataConnectorPerformanceAggregation.actions &&
    dataConnectorPerformanceAggregation.actions.getAll();

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  const onRefreshTable = () =>
    dataConnectorTable &&
    dataConnectorTable.actions &&
    dataConnectorTable.actions.getAll();

  const allTable = dataConnectorTable.data.all;
  const loadingDataTable = dataConnectorTable.data.loading;
  const errorDataTable = dataConnectorTable.data.error;
  const statusDataTable = dataConnectorTable.data.status;

  const allPerf = dataConnectorPerformanceAggregation.data.all;
  const loadingPerf = dataConnectorPerformanceAggregation.data.loading;
  const errorPerf = dataConnectorPerformanceAggregation.data.error;
  const statusPerf = dataConnectorPerformanceAggregation.data.status;

  const PerformanceData = (allPerf as unknown) as IEmaasWaterUsageComponentData;
  const isDataAvailableForPerfromance =
    PerformanceData && PerformanceData.monthWiseAggregations ? true : false;

  const TableData = (allTable as unknown) as IEmaasWaterUsageComponentData;
  const isDataAvailableForTable =
    TableData && TableData.breakdownBySite ? true : false;

  const { all, loading, error, status } = dataConnector.data;
  const electricitySavings = (all as unknown) as IEmaasWaterUsageComponentData;
  const isDataAvailable =
    electricitySavings && electricitySavings.summary ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [isLoadingPerf, setIsLoadingPerf] = useState<boolean>(loadingPerf);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [statusCodePerf, setStatusCodePerf] = useState<number | null>(0);
  const [errorInfoPerf, setErrorInfoPerf] = useState(errorPerf);
  const [data, setData] = useState({} as IEmaasWaterUsageComponentData);
  const [dataPerf, setDataPerf] = useState({} as IEmaasWaterUsageComponentData);

  const [tableData, setTableData] = useState(
    {} as IEmaasWaterUsageComponentData
  );
  const [loadingTable, setLoadingTable] = useState<boolean>(loadingDataTable);
  const [errorTable, setErrorTable] = useState(errorDataTable);
  const [statusCodeTable, setStatusCodeTable] = useState<number | null>(0);

  useEffect(() => {
    setIsLoadingPerf(loadingPerf);
    if (
      isDataAvailableForPerfromance ||
      errorPerf ||
      statusPerf ||
      !loadingPerf
    ) {
      if (isDataAvailableForPerfromance) {
        let tempData: any = PerformanceData;
        //tempData.summary = {};
        setDataPerf(tempData);
      }
      setErrorInfoPerf(errorPerf);
      setStatusCodePerf(statusPerf);
    }
  }, [isDataAvailableForPerfromance, loadingPerf, statusPerf]);

  useEffect(() => {
    setIsLoading(loading);

    if (isDataAvailable || error || status || !loading) {
      if (isDataAvailable) {
        let tempData = electricitySavings;
        setData(tempData);
        setContextData(pollingContext, EmaasEnergyUsage);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  useEffect(() => {
    setLoadingTable(loadingDataTable);

    if (
      isDataAvailableForTable ||
      errorDataTable ||
      statusDataTable ||
      !loadingDataTable
    ) {
      if (isDataAvailableForTable) {
        let tempData: any = TableData;
        // tempData.summary = {};
        // tempData.performanceAggregation = {};
        setTableData(tempData);
      }
      setErrorTable(errorDataTable);
      setStatusCodeTable(statusDataTable);
    }
  }, [isDataAvailableForTable, loadingDataTable, statusDataTable]);

  return isOutOfRange ? (
    <div
      className="noDataDisplay common-error-container"
      style={{ height: 'auto' }}
    >
      <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
    </div>
  ) : (
    <>
      {statusCode == 204 || statusCodePerf == 204 || statusCodeTable == 204 ? (
        // <Card.Content className="kpi-content-container">
        <PortfolioWaterHOCWithLoader
          loading={isLoading}
          error={errorInfo}
          statusCode={204}
          onRefresh={onRefresh}
          data={data}
          loadingText={t(d.LOADING)}
          title={t(d.CONNECTIONERRORTITLE)}
          noDataMessage={t(d.NODATAAVAILABLE)}
          connectionErrorMessage={t(d.CONNECTIONERROR)}
          silentLoading={silentLoading}
          forSummary={false}
        />
      ) : (
        // </Card.Content>
        <>
          <Card.Content className="kpi-content-container">
            <PortfolioWaterHOCWithLoader
              loading={isLoading}
              error={errorInfo}
              statusCode={statusCode}
              onRefresh={onRefresh}
              data={data}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              silentLoading={silentLoading}
              forSummary={true}
            />
          </Card.Content>
          <Card.Content className="kpi-content-container">
            <PortfolioWaterHOCWithLoader
              loading={isLoadingPerf}
              error={errorInfoPerf}
              statusCode={statusCodePerf}
              onRefresh={onRefreshPerf}
              data={dataPerf}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              silentLoading={silentLoading}
              forGraph={true}
            />
          </Card.Content>
          <Card.Content className="kpi-content-container">
            <PortfolioWaterHOCWithLoader
              loading={loadingTable}
              error={errorTable}
              statusCode={statusCodeTable}
              onRefresh={onRefreshTable}
              data={tableData}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              silentLoading={silentLoading}
              forTable={true}
            />
          </Card.Content>
        </>
      )}
    </>
  );
};

const setContextData = (
  pollingContext: IPollingContext,
  EmaasEnergyUsage: IEmaasWaterUsageComponentData
) => {
  const { setPollingTime, setEuiSummary } = pollingContext;

  setPollingTime && setPollingTime();
  setEuiSummary &&
    setEuiSummary(EmaasEnergyUsage?.euiSummary || ({} as IEuiSummary));
};

export default PortfolioWaterHOCSeparate;
